import { useState, useEffect, useRef } from 'react';
import './OptimizedImage.css';

/**
 * OptimizedImage Component
 * Features:
 * - Lazy loading with Intersection Observer
 * - Progressive image loading (blur-up technique)
 * - Responsive images
 * - Error handling with fallback
 */
const OptimizedImage = ({
  src,
  alt = '',
  className = '',
  width,
  height,
  aspectRatio = '1/1',
  objectFit = 'cover',
  loading = 'lazy',
  placeholder = true,
  onLoad,
  onError
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    // Intersection Observer for lazy loading
    if (loading === 'lazy' && imgRef.current && 'IntersectionObserver' in window) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              if (observerRef.current && entry.target) {
                observerRef.current.unobserve(entry.target);
              }
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.01
        }
      );

      observerRef.current.observe(imgRef.current);

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    } else {
      // If not lazy loading or no IntersectionObserver support, load immediately
      setIsInView(true);
    }
  }, [loading]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    setHasError(true);
    if (onError) onError(e);
  };

  const containerStyle = {
    aspectRatio: aspectRatio,
    width: width || '100%',
    height: height || 'auto'
  };

  const imgStyle = {
    objectFit: objectFit
  };

  return (
    <div
      ref={imgRef}
      className={`optimized-image ${className} ${isLoaded ? 'optimized-image--loaded' : ''} ${hasError ? 'optimized-image--error' : ''}`}
      style={containerStyle}
    >
      {placeholder && !isLoaded && !hasError && (
        <div className="optimized-image__placeholder" />
      )}

      {hasError ? (
        <div className="optimized-image__error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <span>Image unavailable</span>
        </div>
      ) : (
        isInView && (
          <img
            src={src}
            alt={alt}
            style={imgStyle}
            className="optimized-image__img"
            onLoad={handleLoad}
            onError={handleError}
            loading={loading}
          />
        )
      )}
    </div>
  );
};

export default OptimizedImage;
